import React, { useState, useEffect } from 'react';

import { withRouter } from 'react-router';
import { getUser } from '../actions/session';
import axiosInstance from '../config/axiosConfig';
import { Notifications } from '../components/common/notification';
import { CDR_STATS_URL, RATEDECK_DOWNLOAD_URL } from '../config/rest_endpoints';

/**
 * TotalStats - Premium Outbound Stats Table
 *
 * Displays key outbound call metrics in a beautifully
 * designed table with smooth animations.
 */
const TotalStats = (props) => {
  const [userInfo] = useState(getUser());
  const [outBoundStats, setOutBoundStats] = useState({});
  const [inBoundStats, setInBoundStats] = useState({});
  const [didStats, setdidStats] = useState({});
  const [balance, setBalance] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    getAllStats();
  }, []);

  const getAllStats = () => {
    if (!userInfo || !userInfo.id) return;
    var userId = userInfo.id;
    // Using axiosInstance which automatically adds auth header and handles 401 errors
    axiosInstance.get(`${CDR_STATS_URL}/${userId}/dashboard`)
      .then((res) => {
        if (res) {
          const resp = res.data.data;
          setOutBoundStats(resp.outboundDashboardStats);
          setInBoundStats(resp.inboundDashboardStats);
          setdidStats(resp.didStats);
          setBalance(resp.balance);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        // 401/JWT expiry is handled by axiosInstance interceptor
        if (error.response && error.response.status !== 401) {
          Notifications.notify(error.message || 'Error', 'Error', 'error');
        }
      });
  };

  const downloadRatedeck = () => {
    const ratedeckID = outBoundStats['ratedeckID'];
    if (!ratedeckID) return;
    setIsDownloading(true);
    axiosInstance.get(`${RATEDECK_DOWNLOAD_URL}?file=${ratedeckID}`, { responseType: 'blob' })
      .then((res) => {
        const blob = new Blob([res.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', outBoundStats['rate'] || 'ratedeck.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setIsDownloading(false);
      })
      .catch((error) => {
        setIsDownloading(false);
        if (error.response && error.response.status !== 401) {
          Notifications.notify('Failed to download ratedeck', 'Error', 'error');
        }
      });
  };

  // Check if rate is a CSV (has ratedeckID)
  const isRateCSV = outBoundStats['ratedeckID'] && outBoundStats['ratedeckID'] !== '';

  // Stats configuration for the table
  const statsConfig = [
    { label: 'Spent', value: outBoundStats['spent'] },
    { label: 'Spent Yesterday', value: outBoundStats['yesterdaySpent'] },
    { label: 'ASR', value: outBoundStats['ASR'] },
    { label: 'ACD', value: outBoundStats['ACD'] },
    { label: 'SDP', value: outBoundStats['SDP'] },
    { label: 'Rate', value: outBoundStats['rate'] === '' ? 'N/A' : outBoundStats['rate'], isRate: true }
  ];

  return (
    <div className="card" style={{ animation: 'fadeInUp 0.5s ease', transition: 'transform 200ms ease' }}>

      <div
        className="table-responsive"
        style={{
          borderRadius: '20px',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div
          className="card-header header-elements-inline"
          style={{
            background: 'var(--color-bg-subtle)',
            padding: '1rem 1.5rem',
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div className="mr-auto">
            <h5
              style={{
                margin: 0,
                fontWeight: 700,
                fontSize: '1rem',
                color: 'var(--color-text)',
                letterSpacing: '-0.02em',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <span
                style={{
                  width: 32,
                  height: 32,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--color-primary-subtle)',
                  borderRadius: '8px',
                  color: 'var(--color-primary)'
                }}
              >
                <i className="icon-phone-outgoing" style={{ fontSize: '1rem' }}></i>
              </span>
              Today's Stats
            </h5>
          </div>
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: 500,
              color: 'var(--color-text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            Today
          </div>
        </div>

        {/* Table */}
        <table className="table text-nowrap" style={{ marginBottom: 0 }}>
          <thead>
            <tr>
              {statsConfig.map((stat, index) => (
                <th
                  key={stat.label}
                  style={{
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-secondary)',
                    fontWeight: 600,
                    fontSize: '0.6875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    padding: '1rem 1.25rem',
                    borderBottom: '1px solid var(--color-divider)'
                  }}
                >
                  {stat.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="right_align">
              {statsConfig.map((stat, index) => (
                <td
                  key={stat.label}
                  style={{
                    padding: '1rem 1.25rem',
                    borderBottom: 'none',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    color: 'var(--color-text)'
                  }}
                >
                  {isLoading ? (
                    <span
                      style={{
                        display: 'inline-block',
                        width: 60,
                        height: 16,
                        background: 'var(--color-bg-subtle)',
                        borderRadius: 4,
                        animation: 'shimmer 1.5s infinite'
                      }}
                    />
                  ) : stat.isRate && isRateCSV ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                      {stat.value || '0'}
                      <button
                        onClick={downloadRatedeck}
                        disabled={isDownloading}
                        title="Download Ratedeck CSV"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: isDownloading ? 'not-allowed' : 'pointer',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          color: 'var(--color-primary)',
                          fontSize: '0.875rem',
                          opacity: isDownloading ? 0.5 : 1
                        }}
                      >
                        {isDownloading ? (
                          <i className="fa fa-spinner fa-spin"></i>
                        ) : (
                          <i className="fa fa-download"></i>
                        )}
                      </button>
                    </span>
                  ) : (
                    stat.value || '0'
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default withRouter(TotalStats);
